import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Share2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { ShareMenu } from '../components/ShareMenu';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { enhanceAccessibleHtml } from '../utils/a11yHtml';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatEventDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const imageUrl = (url) => {
  if (!url) return null;
  return url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
};

const EventDetailDialog = ({ event, open, onOpenChange }) => {
  if (!event) return null;
  const heroSrc = imageUrl(event.hero_image_url) || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800';
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/events?open=${encodeURIComponent(event.event_id)}` : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 gap-0 flex flex-col">
        <DialogHeader className="p-6 pb-4 flex-shrink-0 border-b border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div>
              {event.event_date && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <Calendar className="w-4 h-4" />
                  {formatEventDate(event.event_date)}
                </div>
              )}
              <DialogTitle className="text-2xl font-bold text-[#211F1F] text-left pr-12">
                {event.title}
              </DialogTitle>
            </div>
            <ShareMenu
              url={shareUrl}
              title={event.title}
              description={event.body?.replace(/<[^>]*>/g, '').slice(0, 200)}
              trigger={
                <Button variant="outline" size="sm" className="gap-2 flex-shrink-0">
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
              }
            />
          </div>
        </DialogHeader>

        {/* Rich text writeup at top */}
        {event.body && (
          <div className="p-6 pt-0 flex-shrink-0">
            <div
              className="prose prose-lg max-w-none text-gray-700 prose-headings:text-[#211F1F] prose-a:text-[#9b352c] hover:prose-a:text-[#7a2a1f]"
              dangerouslySetInnerHTML={{ __html: enhanceAccessibleHtml(event.body, { pageTitle: event.title }) }}
            />
          </div>
        )}

        {/* Gallery */}
        {event.gallery_images && event.gallery_images.length > 0 && (
          <div className="p-6 pt-4 border-t border-gray-100">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Gallery</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {event.gallery_images.map((img, idx) => {
                const url = typeof img === 'string' ? img : img?.url;
                const alt = typeof img === 'object' && img?.alt ? img.alt : `Event image ${idx + 1}`;
                const caption = typeof img === 'object' && img?.caption ? img.caption : null;
                return (
                  <div key={idx} className="rounded-xl overflow-hidden bg-gray-100">
                    <div className="aspect-[4/3]">
                      <img
                        src={imageUrl(url)}
                        alt={alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {caption && (
                      <p className="mt-2 text-sm text-gray-600 text-center px-1">{caption}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const EventCard = ({ event, index, onClick }) => {
  const heroSrc = imageUrl(event.hero_image_url) || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <motion.button
        type="button"
        onClick={() => onClick?.(event)}
        aria-label={`View event: ${event.title}`}
        className="group block h-full w-full cursor-pointer text-left"
      >
        <article className="bg-white rounded-3xl overflow-hidden border border-gray-100 hover:border-[#C8473A]/20 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
          <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
            <img
              src={heroSrc}
              alt={event.hero_image_alt || event.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="p-5 flex-1 flex flex-col">
            {event.event_date && (
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <Calendar className="w-4 h-4" />
                {formatEventDate(event.event_date)}
              </div>
            )}
            <h2 className="heading-card-title group-hover:text-[#C8473A] transition-colors line-clamp-2">
              {event.title}
            </h2>
          </div>
        </article>
      </motion.button>
    </motion.div>
  );
};

const EventsPage = () => {
  usePageTitle('Events');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const openEventId = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('open');

  useEffect(() => {
    axios.get(`${API}/admin/events/public/list?limit=50`)
      .then((res) => setEvents(Array.isArray(res.data) ? res.data : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!openEventId || events.length === 0) return;
    const found = events.find((e) => e.event_id === openEventId);
    if (found) setSelectedEvent(found);
  }, [openEventId, events]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-[#211F1F] mb-4">
              <span className="text-[#C8473A]">Events</span>
            </h1>
            <p className="text-lg text-gray-600">
              Join us for exclusive events featuring industry leaders, successful entrepreneurs, and thought leaders.
            </p>
          </motion.div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-3xl overflow-hidden border border-gray-100 animate-pulse">
                  <div className="aspect-[16/10] bg-gray-200" />
                  <div className="p-5">
                    <div className="h-4 bg-gray-100 rounded w-24 mb-3" />
                    <div className="h-6 bg-gray-200 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16 bg-white rounded-2xl border border-gray-100"
            >
              <Calendar className="w-14 h-14 mx-auto text-gray-300 mb-4" />
              <h2 className="heading-empty">No events yet</h2>
              <p className="text-gray-500">Check back later for upcoming events.</p>
            </motion.div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event, index) => (
                  <EventCard
                    key={event.event_id}
                    event={event}
                    index={index}
                    onClick={setSelectedEvent}
                  />
                ))}
              </div>
              <EventDetailDialog
                event={selectedEvent}
                open={!!selectedEvent}
                onOpenChange={(open) => !open && setSelectedEvent(null)}
              />
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EventsPage;
